import React, { useEffect } from 'react';
import { appButtonType, appIconsPath, notificationType, validCharRegex } from '@utils/globalConstant';
import { tA11y } from '@utils/a11y/a11yHelpers';
import { Form, Formik } from 'formik';
import {
  FormInputField,
  ButtonPrimary,
  DropdownList,
  ApiError,
  NotificationSuggestion,
  FocusFirstError
} from '@core/index';
import { useActivation } from '@context/MobileOne';
import { useA11y } from '@context/Utils';
import { PersonalDataLayout } from './PersonalDataLayout';

export function AddressData() {
  // States
  const {
    countries,
    personalDataForm,
    personalDataValidations,
    personalDataFormSubmit,
    validateAddressRes,
    formError,
    setFormError,
    isLoading,
    getLookup
  } = useActivation();
  const { isGenericApiError } = useA11y();

  // Const
  const step7SubStep1Schema = personalDataValidations.pick([
    'street',
    'houseNumber',
    'zip',
    'city',
    'countryCode'
  ]);

  const countryLabelContent = tA11y('ek_global_country_lbl');
  const countryPlaceholderContent = tA11y('ek_global_country_plc');
  const buttonContent = tA11y('ek_reg_adress_btn1');
  const successBoxTitle = tA11y('ek_reg_adress_vldtr_hdl1');
  const successBoxText = tA11y('ek_reg_adress_vldtr_txt1');

  useEffect(() => {
    getLookup();
  }, []);

  const handleAPIErrorLink = () => {
    const firstInput = document.querySelector('input');
    if (firstInput) {
      firstInput.focus();
    }
  }

  return (
    <PersonalDataLayout formTitleKey="ek_reg_adress_data_hdl2" icon={appIconsPath.MARKER1}>
      <div className="w-100 pt-32">
        <Formik
          initialValues={personalDataForm}
          validationSchema={step7SubStep1Schema}
          onSubmit={personalDataFormSubmit}>
          {({
            values: { street, houseNumber, zip, city, countryCode },
            handleBlur,
            handleChange,
            setFieldValue,
            handleSubmit,
            touched,
            errors
          }) => (
            <Form onSubmit={handleSubmit}>
              <FormInputField
                label="ek_global_street_lbl"
                placeholder="ek_global_street_plc"
                type="text"
                id="street"
                name="street"
                value={street}
                onChange={(e) => {
                  handleChange(e);
                  setFormError(false);
                }}
                onClear={setFieldValue}
                onBlur={handleBlur}
                valid={touched.street && !errors.street}
                invalid={(touched.street && errors.street) || !!(formError && !isGenericApiError)}
                invalidMessage={touched.street && errors.street ? errors.street : null}
                aria-describedby="street-error"
              />
              <FormInputField
                customClass="mt-32"
                label="ek_global_house_number_lbl"
                placeholder="ek_global_house_number_plc"
                type="text"
                id="houseNumber"
                name="houseNumber"
                value={houseNumber}
                onChange={(e) => {
                  handleChange(e);
                  setFormError(false);
                }}
                onClear={setFieldValue}
                onBlur={handleBlur}
                valid={touched.houseNumber && !errors.houseNumber}
                invalid={
                  (touched.houseNumber && errors.houseNumber) || !!(formError && !isGenericApiError)
                }
                invalidMessage={
                  touched.houseNumber && errors.houseNumber ? errors.houseNumber : null
                }
                aria-describedby="houseNumber-error"
              />
              <FormInputField
                customClass="mt-32"
                label="ek_global_zip_lbl"
                placeholder="ek_global_zip_plc"
                type="text"
                id="zip"
                name="zip"
                value={zip}
                onChange={(e) => {
                  handleChange(e);
                  setFormError(false);
                }}
                onClear={setFieldValue}
                onBlur={handleBlur}
                valid={touched.zip && !errors.zip}
                invalid={(touched.zip && errors.zip) || !!(formError && !isGenericApiError)}
                invalidMessage={touched.zip && errors.zip ? errors.zip : null}
                aria-describedby="zip-error"
                checkCharRegex={validCharRegex.ZIP_CODE}
              />
              <FormInputField
                customClass="my-32"
                label="ek_global_place_lbl"
                placeholder="ek_global_place_plc"
                type="text"
                id="city"
                name="city"
                value={city}
                onChange={(e) => {
                  handleChange(e);
                  setFormError(false);
                }}
                onClear={setFieldValue}
                onBlur={handleBlur}
                valid={touched.city && !errors.city}
                invalid={(touched.city && errors.city) || !!(formError && !isGenericApiError)}
                invalidMessage={touched.city && errors.city ? errors.city : null}
                aria-describedby="city-error"
              />
              <DropdownList
                id="countryCode"
                name="countryCode"
                label={countryLabelContent?.content}
                value={countryCode}
                placeholder={countryPlaceholderContent?.content}
                onChange={(value) => {
                  setFieldValue('countryCode', value);
                  setFormError(false);
                }}
                onBlur={handleBlur}
                options={(countries || []).map((country) => ({
                  id: country.id,
                  label: country.value
                }))}
                valid={touched.countryCode && !errors.countryCode}
                invalid={
                  (touched.countryCode && errors.countryCode) || !!(formError && !isGenericApiError)
                }
                invalidMessage={
                  touched.countryCode && errors.countryCode ? errors.countryCode : null
                }
                isRequired
                aria-describedby="countryCode-error"
              />
              {formError && (
                <div className="form-text mt-40 ps-4" aria-live="polite">
                  <ApiError
                    message={formError}
                    isGenericApiError={isGenericApiError}
                    handleAPIErrorLink={handleAPIErrorLink}
                  />
                </div>
              )}

              {validateAddressRes && (
                <div className="pt-40">
                  <NotificationSuggestion
                    type={notificationType.VARIANT2}
                    title={successBoxTitle?.content}
                    text={successBoxText?.content}
                    message={[
                      `${validateAddressRes?.street} ${validateAddressRes?.houseNumber}`,
                      `${validateAddressRes?.zip} ${validateAddressRes?.city}`
                    ]}
                  />
                </div>
              )}
              <div className="row">
                <div className="col-xxl-5 col-lg-5 col-10 px-0 mx-auto">
                  <ButtonPrimary
                    buttonClass="mt-40"
                    buttonType={appButtonType.PRIMARY}
                    buttonContent={buttonContent}
                    iconRight={appIconsPath.ARROW_RIGHT}
                    type="submit"
                    isLoading={isLoading}
                  />
                </div>
              </div>
              <FocusFirstError />
            </Form>
          )}
        </Formik>
      </div>
    </PersonalDataLayout>
  );
}

export default AddressData;
